import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, ExternalLink, Linkedin } from 'lucide-react';
import NavBar from '@/components/NavBar';
import MainFooter from '@/components/MainFooter';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ImageOptimizer } from '@/components/common/ImageOptimizer';
import HubSpotForm from '@/components/common/HubSpotForm';
import TallyForm from '@/components/common/TallyForm';
import { getEventById, Event } from '@/data/events';
import AddToCalendarButton from '@/components/common/AddToCalendarButton';

// Parse section content into paragraphs and bullet items
const parseSectionContent = (content: string) => {
  const lines = content
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0);

  const bulletRegex = /^(\u2022|\-|\*)\s+/; // •, -, *
  const bulletIndices: number[] = [];

  lines.forEach((line, index) => {
    if (bulletRegex.test(line)) bulletIndices.push(index);
  });

  if (bulletIndices.length === 0) {
    return {
      paragraphs: [content],
      bullets: [] as string[],
      trailing: [] as string[]
    };
  }

  const firstBullet = bulletIndices[0];
  const lastBullet = bulletIndices[bulletIndices.length - 1];

  const paragraphs = lines.slice(0, firstBullet);
  const bullets = lines
    .slice(firstBullet, lastBullet + 1)
    .filter(line => bulletRegex.test(line))
    .map(line => line.replace(bulletRegex, ''));
  const trailing = lines.slice(lastBullet + 1);

  return { paragraphs, bullets, trailing };
};

// Helper functions for meta tag management
const updateMetaTag = (attribute: string, name: string, content: string) => {
  if (!content) return;
  
  let meta = document.querySelector(`meta[${attribute}="${name}"]`);
  if (meta) {
    meta.setAttribute('content', content);
  } else {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  }
};

const updateCanonicalLink = (url: string) => {
  let canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.setAttribute('href', url);
  } else {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', url);
    document.head.appendChild(canonical);
  }
};

// Countdown timer component for event banner
const EventCountdownBanner: React.FC<{ targetDate: string; targetTime: string }> = ({ targetDate, targetTime }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const target = new Date(`${targetDate}T${targetTime}`);
      const difference = target.getTime() - now.getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate, targetTime]);

  const formatTimeUnit = (value: number, unit: string) => {
    return `${value} ${unit}${value !== 1 ? 's' : ''}`;
  };

  const timeString = [
    timeLeft.days > 0 && formatTimeUnit(timeLeft.days, 'day'),
    timeLeft.hours > 0 && formatTimeUnit(timeLeft.hours, 'hour'),
    timeLeft.minutes > 0 && formatTimeUnit(timeLeft.minutes, 'minute'),
    formatTimeUnit(timeLeft.seconds, 'second')
  ].filter(Boolean).join(', ');

  return (
    <div className="bg-[#858DA0] text-white py-3 px-4 text-center">
      <div className="container mx-auto flex items-center justify-center gap-2">
        <Clock className="h-4 w-4" />
        <span className="font-bold text-sm">
          Event starting in {timeString}
        </span>
      </div>
    </div>
  );
};

const SingleEvent = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const registrationContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (eventId) {
      const foundEvent = getEventById(eventId);
      if (foundEvent) {
        setEvent(foundEvent);
        
        // Set meta tags directly
        const siteOrigin = window.location.origin;
        const pageUrl = window.location.href;
        const description = foundEvent.longDescription || 'Join our upcoming event by Cutshort.';
        const ogImage = foundEvent.image?.startsWith('http') ? foundEvent.image : `${siteOrigin}${foundEvent.image}`;
        const keywords = foundEvent.keywords?.join(', ') || '';
        
        // Update document title
        document.title = `${foundEvent.title} - Cutshort`;
        
        // Update meta tags
        updateMetaTag('name', 'description', description);
        updateMetaTag('name', 'keywords', keywords);
        updateMetaTag('property', 'og:title', `${foundEvent.title} - Cutshort`);
        updateMetaTag('property', 'og:description', description);
        updateMetaTag('property', 'og:type', 'website');
        updateMetaTag('property', 'og:url', pageUrl);
        updateMetaTag('property', 'og:image', ogImage);
        updateMetaTag('name', 'twitter:title', `${foundEvent.title} - Cutshort`);
        updateMetaTag('name', 'twitter:description', description);
        updateMetaTag('name', 'twitter:image', ogImage);
        updateMetaTag('property', 'twitter:card', 'summary_large_image');
        
        // Update canonical link
        updateCanonicalLink(pageUrl);
        
        console.log('Event loaded:', foundEvent.title);
      } else {
        console.log('Event not found:', eventId);
        navigate('/events');
      }
    }
  }, [eventId, navigate]);

  // Load Luma checkout script if needed
  useEffect(() => {
    if (!event?.lumaEventId) return;
    const existing = document.getElementById('luma-checkout');
    if (existing) return;
    const script = document.createElement('script');
    script.id = 'luma-checkout';
    script.src = 'https://embed.lu.ma/checkout-button.js';
    script.async = true;
    document.body.appendChild(script);
  }, [event?.lumaEventId]);

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto mb-4"></div>
          <p className="text-brand-grey">Loading event...</p>
        </div>
      </div>
    );
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'webinar':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'event':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'workshop':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'webinar':
        return 'Webinar';
      case 'event':
        return 'Event';
      case 'workshop':
        return 'Workshop';
      default:
        return 'Event';
    }
  };

  // Check if event is happening in the next 14 days
  const isEventWithin14Days = (eventDate: string, eventTime: string) => {
    const now = new Date();
    const eventDateTime = new Date(`${eventDate}T${eventTime}`);
    const timeDifference = eventDateTime.getTime() - now.getTime();
    const daysDifference = timeDifference / (1000 * 60 * 60 * 24);
    return daysDifference > 0 && daysDifference <= 14;
  };

  // Check if event is over
  const isEventOver = (eventDate: string, eventTime: string) => {
    const now = new Date();
    const eventDateTime = new Date(`${eventDate}T${eventTime}`);
    return now.getTime() > eventDateTime.getTime();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />
      <main className="pt-20">
      
      {/* Countdown Banner - Show if event is within 14 days */}
      {event && isEventWithin14Days(event.date, event.time) && (
        <EventCountdownBanner targetDate={event.date} targetTime={event.time} />
      )}
      
      {/* Hero Section */}
      <div className="bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Event details */}
            <div>
              <div className="flex flex-wrap gap-3 mb-4">
                <Badge 
                  variant="outline" 
                  className={`${getCategoryColor(event.category)} border`}
                >
                  {event.seriesName || getCategoryLabel(event.category)}
                </Badge>
                
                {event.specialType === 'off-the-record' && (
                  <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 font-semibold shadow-lg">
                    🔒 OFF THE RECORD
                  </Badge>
                )}
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-brand-darkgrey mb-4 leading-tight">
                {event.title}
              </h1>
              
              {event.subtitle && (
                <h2 className="text-2xl text-brand-grey mb-6 leading-relaxed">
                  {event.subtitle}
                </h2>
              )}
              
              <p className="text-xl text-brand-grey mb-8 leading-relaxed">
                {event.longDescription}
              </p>
              
              {/* Event metadata */}
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 text-brand-darkgrey">
                  <Calendar className="h-5 w-5 text-brand-blue" />
                  <span className="font-medium">
                    {new Date(event.date).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                  {isEventOver(event.date, event.time) && (
                    <Badge variant="outline" className="ml-2 bg-red-50 text-red-700 border-red-200">
                      This event is over
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-3 text-brand-darkgrey">
                  <Clock className="h-5 w-5 text-brand-blue" />
                  <span className="font-medium">{event.time} ({event.timezone || 'EST'})</span>
                </div>
                
                <div className="flex items-center gap-3 text-brand-darkgrey">
                  <MapPin className="h-5 w-5 text-brand-blue" />
                  <span className="font-medium">Virtual Event</span>
                </div>
              </div>
              
              {/* Tags */}
              {event.tags && event.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-8">
                  {event.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="bg-gray-100 text-gray-700">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}

            </div>
            
            {/* Right side - Registration */}
            <div className="lg:pl-8" ref={registrationContainerRef}>
              {isEventOver(event.date, event.time) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-amber-800 text-sm font-medium">
                    This event is over but to get the content and other resources, you can still register
                  </p>
                </div>
              )}
              
              {event.lumaEventId ? (
                <div className="bg-white rounded-lg shadow-lg p-6 sticky top-8 text-center">
                  <h3 className="text-xl font-semibold text-brand-darkgrey mb-2">Register for this event</h3>
                  <p className="text-brand-grey text-sm mb-4">Secure your spot today</p>
                  <div className="flex items-center justify-center gap-3">
                    <a
                      href={`https://luma.com/event/${event.lumaEventId}`}
                      data-luma-action="checkout"
                      data-luma-event-id={event.lumaEventId}
                      className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-brand-blue text-white hover:bg-brand-blue/90 transition-colors"
                    >
                      Register for Event
                    </a>
                    <AddToCalendarButton
                      title={event.title}
                      description={event.longDescription}
                      location="Virtual Event"
                      date={event.date}
                      time={event.time}
                      timezone={event.timezone}
                      durationMinutes={60}
                    />
                  </div>
                  <p className="text-xs text-brand-grey mt-4">Powered by Luma</p>
                </div>
              ) : event.hubspotFormId ? (
                <HubSpotForm 
                  formId={event.hubspotFormId}
                  title="Register for this event"
                  subtitle="Secure your spot today"
                  className="sticky top-8"
                />
              ) : event.tallyFormEmbed ? (
                <TallyForm 
                  embedUrl={event.tallyFormEmbed}
                  title="Register for this event"
                  subtitle="Secure your spot today"
                  className="sticky top-8"
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      {event.sections && event.sections.length > 0 && (
        <div className="bg-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {event.sections.map((section) => (
                <div key={section.id} className="mb-16 last:mb-0">
                  <h2 className="text-3xl font-bold text-brand-darkgrey mb-6 text-center">
                    {section.heading}
                  </h2>
                  <div className="prose prose-lg max-w-none text-left">
                    {(() => {
                      const { paragraphs, bullets, trailing } = parseSectionContent(section.content);
                      return (
                        <>
                          {paragraphs.map((p, idx) => (
                            <p key={`p-${idx}`} className="text-lg text-brand-grey leading-relaxed">
                              {p}
                            </p>
                          ))}
                          {bullets.length > 0 && (
                            <ul className="list-disc pl-6 my-6 text-brand-grey text-lg space-y-2">
                              {bullets.map((item, idx) => (
                                <li key={`b-${idx}`}>{item}</li>
                              ))}
                            </ul>
                          )}
                          {trailing.map((p, idx) => (
                            <p key={`t-${idx}`} className="text-lg text-brand-grey leading-relaxed">
                              {p}
                            </p>
                          ))}
                        </>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Speakers Section */}
      {event.speakers && event.speakers.length > 0 && (
        <div className="bg-gray-50 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-brand-darkgrey text-center mb-12">
                Meet the Guests
              </h2>
              
              <div className="flex flex-wrap justify-center gap-8">
                {event.speakers.map((speaker) => (
                  <div key={speaker.id} className="bg-white rounded-lg shadow-lg p-6 text-center w-full max-w-md relative">
                    {event.specialType === 'off-the-record' && (
                      <Badge className="absolute top-4 left-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 font-semibold shadow-md">
                        🔒 OFF THE RECORD
                      </Badge>
                    )}
                    <div className="mb-4">
                      <ImageOptimizer
                        src={speaker.image}
                        alt={speaker.name}
                        className="w-24 h-24 rounded-full mx-auto object-cover"
                        width={96}
                        height={96}
                        loading="lazy"
                        objectFit="cover"
                      />
                    </div>
                    
                    <h3 className="text-xl font-semibold text-brand-darkgrey mb-2">
                      {speaker.name}
                    </h3>
                    
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <Badge 
                        variant="outline" 
                        className="text-xs px-2 py-1 border-brand-blue text-brand-blue"
                      >
                        {speaker.role}
                      </Badge>
                    </div>
                    
                    <p className="text-brand-blue font-medium mb-3">
                      {speaker.title}
                    </p>
                    
                    <p className="text-brand-grey text-sm mb-4 leading-relaxed">
                      {speaker.bio}
                    </p>
                    
                    {event.specialType !== 'off-the-record' && (
                      <a
                        href={speaker.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-brand-blue hover:text-brand-blue/80 transition-colors"
                      >
                        <Linkedin className="h-5 w-5" />
                        <span className="text-sm">Connect on LinkedIn</span>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Registration CTA Section */}
      <div className="bg-brand-blue py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Seats run out fast. Register now.
            </h2>
            
            <p className="text-xl text-blue-100 mb-8">
              Don't miss this opportunity to learn from industry experts and network with peers.
            </p>
            
            <div className="flex items-center justify-center gap-3">
              {event.lumaEventId ? (
                <a
                  href={`https://luma.com/event/${event.lumaEventId}`}
                  data-luma-action="checkout"
                  data-luma-event-id={event.lumaEventId}
                  className="inline-flex items-center justify-center bg-white text-brand-blue hover:bg-gray-100 text-lg px-8 py-4 rounded-md"
                >
                  Register Now
                </a>
              ) : event.hubspotFormId ? (
                <Button
                  size="lg"
                  className="bg-white text-brand-blue hover:bg-gray-100 text-lg px-8 py-4"
                  onClick={() => {
                    if (registrationContainerRef.current) {
                      registrationContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  Register Now
                </Button>
              ) : null}
              <AddToCalendarButton
                title={event.title}
                description={event.longDescription}
                location="Virtual Event"
                date={event.date}
                time={event.time}
                timezone={event.timezone}
                durationMinutes={60}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Registration Form Modal */}
      {showRegistrationForm && event.hubspotFormId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-brand-darkgrey">
                  Register for {event.title}
                </h3>
                <button
                  onClick={() => setShowRegistrationForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <HubSpotForm 
                formId={event.hubspotFormId}
                title=""
                subtitle=""
              />
            </div>
          </div>
        </div>
      )}

      {/* Newsletter CTA Section removed as per request */}
      </main>

      <MainFooter />
    </div>
  );
};

export default SingleEvent;

